import { getVisitorId, getSessionId } from './visitor-identity';
import { whenBigBrainReady } from './bigbrain-init';

export const PAGE_VIEW_EVENT = 'ai_app_runtime_page_view';

const MAX_TITLE_LENGTH = 60;
const MAX_BUFFERED_VIEWS = 20;
const ENTRY_KEY_PREFIX = 'vibe-public-app:page-view-entry:';

let viewContext = null;
let bufferedViews = [];
const memoryEntrySessions = new Set();

const RAW_APP_ID = '__VIBE_APP_ID__';

function readDefinedAppId() {
  try {
    // eslint-disable-next-line no-undef
    return typeof __VIBE_APP_ID__ !== 'undefined' ? __VIBE_APP_ID__ : undefined;
  } catch {
    return undefined;
  }
}

function positiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function resolveAppId(context) {
  const substituted = String(RAW_APP_ID).includes('__VIBE_') ? undefined : positiveNumber(RAW_APP_ID);
  return substituted ?? positiveNumber(readDefinedAppId()) ?? positiveNumber(context?.aiAppId ?? context?.appId) ?? 0;
}

function resolveUserId(context) {
  const rawId = context?.user?.id ?? context?.userId ?? null;
  return rawId == null || !Number.isFinite(Number(rawId)) ? null : Number(rawId);
}

function resolveAccountId(context) {
  return positiveNumber(context?.account?.id ?? context?.accountId) ?? null;
}

export function derivePageTitle(pattern) {
  try {
    const segments = String(pattern ?? '')
      .split('/')
      .filter(Boolean);
    if (segments.length === 0) return 'Home';
    const parts = segments.map((segment) => {
      if (segment.startsWith(':') || segment.startsWith('$') || /^\[.*\]$/.test(segment)) return 'detail';
      const words = segment.replace(/[-_]+/g, ' ').trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    });
    return parts.join(' › ') || 'Home';
  } catch {
    return 'Home';
  }
}

export function resolvePageTitle(candidate, pattern) {
  try {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed.length >= 1 && trimmed.length <= MAX_TITLE_LENGTH) return trimmed;
    }
  } catch {}
  return derivePageTitle(pattern);
}

function hostOf(referrer) {
  try {
    if (!referrer) return null;
    const host = new URL(referrer).hostname.replace(/^www\./, '');
    return host || null;
  } catch {
    return null;
  }
}

export function classifyTrafficSource(search, referrer, inMonday) {
  try {
    const referrer_host = hostOf(referrer);
    if (inMonday) return { traffic_source_kind: 'monday', traffic_source: 'monday', referrer_host };
    const params = new URLSearchParams(search ?? '');
    const campaign = params.get('utm_source') || params.get('kind');
    if (campaign) return { traffic_source_kind: 'campaign', traffic_source: campaign, referrer_host };
    if (referrer_host) return { traffic_source_kind: 'referrer', traffic_source: referrer_host, referrer_host };
    return { traffic_source_kind: 'direct', traffic_source: null, referrer_host: null };
  } catch {
    return { traffic_source_kind: 'direct', traffic_source: null, referrer_host: null };
  }
}

export function detectDeviceType(nav) {
  try {
    const mobile = nav?.userAgentData?.mobile;
    if (typeof mobile === 'boolean') return mobile ? 'mobile' : 'desktop';
    return /Mobi|Android|iPhone|iPad|iPod/i.test(nav?.userAgent ?? '') ? 'mobile' : 'desktop';
  } catch {
    return 'desktop';
  }
}

export function isBuilderPreview(win, context) {
  try {
    if (context?.builderPreview === true) return true;
    try {
      if (new URLSearchParams(win.location.search).get('builderPreview') === 'true') return true;
    } catch {}
    return (win.location.hostname || '').split('.')[0].endsWith('-draft');
  } catch {
    return false;
  }
}

function resolvePlacement(context) {
  const value = context?.viewType ?? context?.instanceType;
  return typeof value === 'string' && value ? value : 'monday';
}

export function buildPageViewPayload({ context, page, pageTitle, isEntry }, win) {
  try {
    const w = win ?? (typeof window !== 'undefined' ? window : undefined);
    const loc = w?.location ?? {};
    const pulse_user_id = resolveUserId(context);
    const viewer_type = pulse_user_id != null ? 'internal' : 'external';
    const app_id = resolveAppId(context);
    const traffic = classifyTrafficSource(loc.search ?? '', w?.document?.referrer ?? '', pulse_user_id != null);
    const pagePattern = typeof page === 'string' && page ? page : '/';
    if (traffic.traffic_source_kind === 'monday') traffic.traffic_source = resolvePlacement(context);

    return {
      info1: String(app_id),
      info2: viewer_type,
      data: {
        app_id,
        viewer_type,
        pulse_user_id,
        account_id: resolveAccountId(context),
        is_creator: Boolean(context?.isCreator),
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        deployment_url: `${loc.origin ?? ''}${loc.pathname ?? ''}`,
        page: pagePattern,
        page_title: resolvePageTitle(pageTitle, pagePattern),
        is_entry: Boolean(isEntry),
        traffic_source_kind: traffic.traffic_source_kind,
        traffic_source: traffic.traffic_source,
        referrer_host: traffic.referrer_host,
        device_type: detectDeviceType(w?.navigator),
      },
    };
  } catch {
    return null;
  }
}

function consumeEntryFlag() {
  const sessionId = getSessionId();
  const key = `${ENTRY_KEY_PREFIX}${sessionId}`;
  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, '1');
    return true;
  } catch {
    if (memoryEntrySessions.has(sessionId)) return false;
    memoryEntrySessions.add(sessionId);
    return true;
  }
}

function dispatch(page, pageTitle) {
  try {
    if (typeof window === 'undefined') return;
    if (isBuilderPreview(window, viewContext)) return;
    const payload = buildPageViewPayload(
      { context: viewContext, page, pageTitle, isEntry: consumeEntryFlag() },
      window
    );
    if (!payload) return;
    whenBigBrainReady()
      .then(() => {
        try {
          if (typeof window.BigBrain !== 'function') return;
          window.BigBrain('track', PAGE_VIEW_EVENT, payload);
        } catch {}
      })
      .catch(() => {});
  } catch {}
}

export function setPageViewContext(context) {
  try {
    viewContext = context ?? {};
    const pending = bufferedViews;
    bufferedViews = [];
    for (const view of pending) dispatch(view.page, view.pageTitle);
  } catch {}
}

export function trackPageView(page, pageTitle) {
  try {
    if (typeof window === 'undefined') return;
    if (isBuilderPreview(window, viewContext)) return;
    if (viewContext === null) {
      if (bufferedViews.length < MAX_BUFFERED_VIEWS) bufferedViews.push({ page, pageTitle });
      return;
    }
    dispatch(page, pageTitle);
  } catch {}
}
